import { memo, useEffect } from "react";
import { useParams } from "react-router";
import { Card } from "flowbite-react";
import { Building2, MoveRight, UserRoundCheck } from "lucide-react";
import { AnimatePresence, motion, useAnimation } from "motion/react";
import yeatiLogo from "@/assets/images/yeati-logo.png";
import { InfoStep } from "@/features/employeeTransfer/components/InfoStep/InfoStep";
import { OtpStep } from "@/features/employeeTransfer/components/OtpStep/OtpStep";
import { ErrorScreen } from "@/features/employeeTransfer/components/TerminalScreens/ErrorScreen";
import { FailedScreen } from "@/features/employeeTransfer/components/TerminalScreens/FailedScreen";
import { ProcessingScreen } from "@/features/employeeTransfer/components/TerminalScreens/ProcessingScreen";
import { RefusedScreen } from "@/features/employeeTransfer/components/TerminalScreens/RefusedScreen";
import { SuccessScreen } from "@/features/employeeTransfer/components/TerminalScreens/SuccessScreen";
import { TransferSkeleton } from "@/features/employeeTransfer/components/TransferSkeleton";
import { useEmployeeTransferWizard } from "@/features/employeeTransfer/hooks/useEmployeeTransferWizard";

const ARROW_LOOP_TRANSITION = {
  duration: 1.4,
  repeat: Infinity,
  repeatType: "loop",
  ease: "linear",
} as const;

const HERO_CONTAINER_VARIANTS = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.4 } },
};

const LEFT_BUILDING_VARIANTS = {
  hidden: { opacity: 0, x: -16 },
  visible: { opacity: 1, x: 0, transition: { type: "spring" as const, stiffness: 120, damping: 14 } },
};

const USER_CHECK_VARIANTS = {
  hidden: { opacity: 0, scale: 0.6 },
  visible: { opacity: 1, scale: 1, transition: { type: "spring" as const, stiffness: 160, damping: 12 } },
};

const RIGHT_BUILDING_VARIANTS = {
  hidden: { opacity: 0, x: 16 },
  visible: { opacity: 1, x: 0, transition: { type: "spring" as const, stiffness: 120, damping: 14 } },
};

const ARROW_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
};

const STEP_ANIMATION = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.2 },
};

const LoopingArrow = () => (
  <motion.div className="w-8 overflow-hidden" variants={ARROW_VARIANTS}>
    <motion.div animate={{ x: [-32, 0] }} transition={ARROW_LOOP_TRANSITION} className="flex items-center gap-[8px]">
      <MoveRight size={24} className="text-teal-500 dark:text-teal-400 shrink-0" />
      <MoveRight size={24} className="text-teal-500 dark:text-teal-400 shrink-0" />
    </motion.div>
  </motion.div>
);

type WizardResult = ReturnType<typeof useEmployeeTransferWizard>;

const WizardStepContent = memo(({ wizard }: { wizard: WizardResult }) => {
  if (wizard.isLoading) {
    return <TransferSkeleton />;
  }

  if (wizard.step === "error" && wizard.errorKind) {
    return (
      <ErrorScreen
        errorKind={wizard.errorKind}
        onResend={wizard.isResendable ? wizard.handleResendEmail : undefined}
        resendRequested={wizard.resendRequested}
        isMutating={wizard.isMutating}
      />
    );
  }

  if (wizard.step === "processing" && wizard.transferRequest) {
    return <ProcessingScreen percent={wizard.percent} transferRequest={wizard.transferRequest} />;
  }

  if (wizard.step === "failed" && wizard.transferRequest) {
    return <FailedScreen transferRequest={wizard.transferRequest} />;
  }

  if (wizard.step === "success" && wizard.transferRequest) {
    return <SuccessScreen transferRequest={wizard.transferRequest} />;
  }

  if (wizard.step === "refused" && wizard.transferRequest) {
    return <RefusedScreen transferRequest={wizard.transferRequest} />;
  }

  if (wizard.step === "otp" && wizard.phoneHint) {
    return (
      <OtpStep
        phoneHint={wizard.phoneHint}
        isMutating={wizard.isMutating}
        otpError={wizard.otpError}
        onVerify={wizard.handleVerifyOtp}
        onResend={wizard.handleResendOtp}
        description={wizard.pendingAction === "refuse"
          ? "Pour sécuriser le refus, un code de vérification a été envoyé par SMS au numéro"
          : undefined}
      />
    );
  }

  if (wizard.step === "info" && wizard.transferRequest) {
    return (
      <InfoStep
        transferRequest={wizard.transferRequest}
        isMutating={wizard.isMutating}
        inlineError={wizard.inlineError}
        onConfirm={wizard.handleConfirm}
        onRefuse={wizard.handleRefuse}
      />
    );
  }

  return null;
});
WizardStepContent.displayName = "WizardStepContent";

export const EmployeeTransferPage = () => {
  const { token } = useParams<{ token: string }>();

  const wizard = useEmployeeTransferWizard(token ?? "");

  const heroControls = useAnimation();

  useEffect(() => {
    const startAnimation = () => heroControls.start("visible");

    if (document.readyState === "complete") {
      startAnimation();
      return;
    }

    window.addEventListener("load", startAnimation, { once: true });
    return () => window.removeEventListener("load", startAnimation);
  }, [heroControls]);

  return (
    <section
      data-testid="transfer-page-container"
      className="relative min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-teal-950"
    >
      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center px-4 py-10">
        <div className="mb-8 flex flex-col items-center gap-5">
          <img src={yeatiLogo} alt="Yeati" width="240" height="auto" className="drop-shadow-sm" />
          <motion.div
            className="flex items-center gap-4"
            data-testid="transfer-hero-icons"
            aria-hidden="true"
            initial="hidden"
            animate={heroControls}
            variants={HERO_CONTAINER_VARIANTS}
          >
            <motion.div variants={LEFT_BUILDING_VARIANTS}>
              <Building2 size={36} className="text-gray-400 dark:text-gray-500" />
            </motion.div>
            <LoopingArrow />
            <motion.div variants={USER_CHECK_VARIANTS}>
              <UserRoundCheck size={32} className="text-teal-600 dark:text-teal-400" />
            </motion.div>
            <LoopingArrow />
            <motion.div variants={RIGHT_BUILDING_VARIANTS}>
              <Building2 size={36} className="text-teal-600 dark:text-teal-400" />
            </motion.div>
          </motion.div>
        </div>

        <Card className="w-full max-w-[520px] space-y-6 border border-teal-100 bg-white/95 p-6 shadow-md backdrop-blur dark:border-teal-900 dark:bg-gray-900/80">
          <AnimatePresence mode="wait">
            <motion.div key={wizard.step} {...STEP_ANIMATION}>
              <WizardStepContent wizard={wizard} />
            </motion.div>
          </AnimatePresence>
        </Card>
      </div>
    </section>
  );
};
